import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  standalone: false,
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styleUrls: ['./filter-dropdown.component.scss'],
})
export class FilterDropdownComponent implements OnDestroy {
  private static nextId: number = 0;
  private static openInstance: FilterDropdownComponent | null = null; // make sure only one of these menus is open at one time
  private readonly instanceId: number = FilterDropdownComponent.nextId++;

  @Input() label: string = '';
  @Input() valueText: string = '';
  @Input() dimValue: boolean = false;
  @Input() iconSvg: string = '';
  @Input() panelWidth: number = 240;
  @Input() panelClass: string = '';
  @Input() disabled: boolean = false;
  @Input() invalid: boolean = false;
  @Input() ariaLabel: string = '';
  @Input() ariaDescribedBy: string | null = null;
  @Output() readonly openedChange = new EventEmitter<boolean>();

  @ViewChild('root', { static: true }) rootRef!: ElementRef<HTMLElement>;
  @ViewChild('triggerButton', { static: true }) triggerButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') panelRef?: ElementRef<HTMLElement>;

  isOpen: boolean = false;
  openAbove: boolean = false;
  private placementTimeoutIds: number[] = [];
  private panelResizeObserver: ResizeObserver | null = null;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get triggerLabelId(): string {
    return `filter-dropdown-label-${this.instanceId}`;
  }

  get panelId(): string {
    return `filter-dropdown-panel-${this.instanceId}`;
  }

  get usesDateBackdrop(): boolean {
    return this.panelClass.split(' ').includes('date-dropdown-panel');
  }

  toggleOpen(): void {
    if (this.disabled) {
      return;
    } else if (this.isOpen) {
      this.close();
      return;
    } else if (
      FilterDropdownComponent.openInstance &&
      FilterDropdownComponent.openInstance !== this
    ) {
      FilterDropdownComponent.openInstance.close();
    }

    this.isOpen = true;
    FilterDropdownComponent.openInstance = this;
    this.openedChange.emit(true);
    this.queuePanelPlacementChecks();
    this.startPanelResizeObserverOnNextFrame();
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.openAbove = false;
    this.clearPlacementChecks();
    this.stopPanelResizeObserver();

    if (FilterDropdownComponent.openInstance === this) {
      FilterDropdownComponent.openInstance = null;
    }

    this.openedChange.emit(false);
  }

  onBackdropClick(): void {
    this.close();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.queuePanelPlacementChecks();
  }

  @HostListener('window:orientationchange')
  onOrientationChange(): void {
    this.queuePanelPlacementChecks();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isOpen) {
      return;
    }

    this.updatePanelPlacement();
  }

  focusTrigger(): void {
    this.triggerButtonRef?.nativeElement?.focus();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || !this.rootRef) {
      return;
    }

    const isDatepickerOpen: boolean = !!document.querySelector(
      '.mat-datepicker-content, .mat-mdc-datepicker-content'
    );

    if (this.usesDateBackdrop && isDatepickerOpen) {
      return;
    }

    const host: HTMLElement = this.rootRef.nativeElement;
    const target: Node = event.target as Node | null;
    const targetElement: Element =
      target instanceof Element ? target : (target?.parentElement ?? null);
    const clickedInOverlay: boolean = !!targetElement?.closest('.cdk-overlay-container');

    if (clickedInOverlay) {
      return;
    }

    if (target && !host.contains(target)) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this.clearPlacementChecks();
    this.stopPanelResizeObserver();

    if (FilterDropdownComponent.openInstance === this) {
      FilterDropdownComponent.openInstance = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    if (!this.isOpen || !this.rootRef) {
      return;
    }

    const isDatepickerOpen: boolean = !!document.querySelector(
      '.mat-datepicker-content, .mat-mdc-datepicker-content'
    );

    if (this.usesDateBackdrop && isDatepickerOpen) {
      return;
    }

    const host: HTMLElement = this.rootRef.nativeElement;
    const target: Node = event.target as Node | null;
    const targetElement: Element =
      target instanceof Element ? target : (target?.parentElement ?? null);
    const focusedInOverlay: boolean = !!targetElement?.closest('.cdk-overlay-container');

    if (focusedInOverlay) {
      return;
    }

    if (target && !host.contains(target)) {
      this.close();
    }
  }

  private queuePanelPlacementChecks(): void {
    if (!this.isOpen) {
      return;
    }

    this.clearPlacementChecks();

    // Re-check after render and after open animation settles.
    const delays: number[] = [0, 40, 130];
    this.placementTimeoutIds = delays.map((delay: number) =>
      window.setTimeout(() => {
        this.updatePanelPlacement();
      }, delay)
    );
  }

  private clearPlacementChecks(): void {
    this.placementTimeoutIds.forEach((id: number) => window.clearTimeout(id));
    this.placementTimeoutIds = [];
  }

  private startPanelResizeObserverOnNextFrame(): void {
    if (typeof ResizeObserver === 'undefined' || !this.isOpen) {
      return;
    }

    window.setTimeout(() => {
      const panel: HTMLElement | undefined = this.panelRef?.nativeElement;
      if (!panel || !this.isOpen) {
        return;
      }

      this.stopPanelResizeObserver();
      this.panelResizeObserver = new ResizeObserver(() => {
        this.updatePanelPlacement();
      });
      this.panelResizeObserver.observe(panel);
    });
  }

  private stopPanelResizeObserver(): void {
    this.panelResizeObserver?.disconnect();
    this.panelResizeObserver = null;
  }

  private updatePanelPlacement(): void {
    if (!this.isOpen) {
      return;
    }

    const trigger: HTMLButtonElement | undefined = this.triggerButtonRef?.nativeElement;
    const panel: HTMLElement | undefined = this.panelRef?.nativeElement;
    if (!trigger || !panel) {
      return;
    }

    const viewportPadding: number = 8;
    const triggerRect: DOMRect = trigger.getBoundingClientRect();
    const panelRect: DOMRect = panel.getBoundingClientRect();
    const panelHeight: number = Math.max(panelRect.height, panel.scrollHeight);
    const viewportHeight: number = window.visualViewport?.height ?? window.innerHeight;

    const spaceBelow: number = viewportHeight - triggerRect.bottom - viewportPadding;
    const spaceAbove: number = triggerRect.top - viewportPadding;
    const openAboveBuffer: number = 25;
    const shouldOpenAbove: boolean =
      panelHeight > spaceBelow + openAboveBuffer && spaceAbove > spaceBelow;

    if (this.openAbove !== shouldOpenAbove) {
      this.openAbove = shouldOpenAbove;
      this.cdr.detectChanges();
      return;
    }

    this.openAbove = shouldOpenAbove;
  }
}
